import { motion, AnimatePresence } from 'framer-motion';
import { useDrag } from '@use-gesture/react';
import { useEffect, useRef } from 'react';
import type mapboxgl from 'mapbox-gl';
import type { Unit } from '@/types';
import type { UseMobileDrawerReturn } from '../../hooks/useMobileDrawer';
import { MobileInfoDrawerContent } from './MobileInfoDrawerContent';
import { X } from 'lucide-react';

interface MobileInfoDrawerProps {
  drawer: UseMobileDrawerReturn;
  map: mapboxgl.Map | null;
  allUnits?: Unit[]; // All units for zoom calculation
  updateHoverLayer: (unit: Unit | null, currentZoom: number) => void;
  currentZoom: number;
}

export function MobileInfoDrawer({ drawer, map, allUnits, updateHoverLayer, currentZoom }: MobileInfoDrawerProps) {
  const { isOpen, drawerContent, dragProgress, closeDrawer, handleDrag, handleDragEnd } = drawer;

  // Track previous content type to detect transitions between same types (e.g. unit -> unit)
  const prevTypeRef = useRef<string | null>(null);
  const isSwitchingUnits = 
    isOpen && 
    prevTypeRef.current === 'unit_tooltip' && 
    drawerContent?.type === 'unit_tooltip';

  useEffect(() => {
    if (isOpen && drawerContent) {
      prevTypeRef.current = drawerContent.type;
    } else if (!isOpen) {
      // Reset when closed so next open animates
      // We use a small timeout or just reset immediately if we want fresh animation on reopen
      // But since isOpen check handles the "closed" state, we just need to ensure
      // we don't carry over stale type when reopening.
      prevTypeRef.current = null;
    }
  }, [isOpen, drawerContent]);

  // Determine if this is an individual unit (no backdrop) or cluster (with backdrop)
  const isIndividualUnit = drawerContent?.type === 'unit_tooltip';
  const showBackdrop = !isIndividualUnit;

  // Show hover effect and center map on selected unit/cluster
  useEffect(() => {
    if (!map || !isOpen || !drawerContent) return;

    // Get coordinates based on content type
    let unit = null;
    let coordinates: [number, number] | null = null;

    if (drawerContent.type === 'unit_tooltip') {
      unit = drawerContent.unit;
      coordinates = [drawerContent.unit.location.lng, drawerContent.unit.location.lat];
    } else {
      // For clusters, use the cluster coordinates
      coordinates = drawerContent.coordinates;
    }

    // Show hover effect for individual units
    if (unit) {
      updateHoverLayer(unit, currentZoom);
    }

    // Center map on the marker, compensating for drawer height
    if (coordinates) {
      const viewportHeight = window.innerHeight;

      if (isIndividualUnit) {
        // Mobile Layout:
        // - Header (56px) + StatusBar (48px) = 104px (Top fixed UI)
        // - MobileTabNavigation = 64px (Bottom fixed UI)
        // - Drawer = 40vh (Top, below header)
        
        const headerHeight = 104;
        const navHeight = 64;
        const drawerHeightVh = 40; 
        const drawerHeightPx = (drawerHeightVh / 100) * viewportHeight;
        
        // Calculate unobstructed area between bottom of drawer and top of nav
        const unobstructedTop = headerHeight + drawerHeightPx;
        const unobstructedBottom = viewportHeight - navHeight;
        
        // Target Y center for the marker (in screen coordinates)
        const targetCenterY = (unobstructedTop + unobstructedBottom) / 2;
        
        // Calculate offset relative to viewport center
        // Positive Y offset moves the target point DOWN from center
        const offsetY = targetCenterY - (viewportHeight / 2) - 20;

        map.easeTo({
          center: coordinates,
          offset: [0, offsetY],
          duration: 300,
        });
      } else {
        // For clusters, keeping existing/default behavior (centering)
        // or adjusting if needed. Since clusters often take more space or have backdrop,
        // centering is usually safe or we can apply a small offset if needed.
        // Using 0 offset for now as drawer covers most of screen.
        map.easeTo({
          center: coordinates,
          offset: [0, 0],
          duration: 300,
        });
      }
    }

    // Clear hover effect when drawer closes or unmounts
    return () => {
      if (map) {
        updateHoverLayer(null, currentZoom);
      }
    };
  }, [isOpen, drawerContent, map, updateHoverLayer, currentZoom, isIndividualUnit]);

  // Gesture handling for swipe up to close
  const bind = useDrag(
    ({ movement: [, my], velocity: [, vy], last }) => {
      if (last) {
        // Drag ended - check if should close
        handleDragEnd(vy);
      } else {
        // Actively dragging
        handleDrag(my);
      }
    },
    {
      axis: 'y',
      filterTaps: true,
      threshold: 10,
    }
  );

  // Calculate transform based on drag progress
  // 0 = fully open (0% from top), 1 = fully closed (-100% slides up/away)
  const drawerTransform = -dragProgress * 100;
  const backdropOpacity = 0.4 * (1 - dragProgress);

  // Height calculation: 40vh for individual units, full height for clusters
  const drawerHeight = isIndividualUnit
    ? '40vh'
    : 'calc(100dvh - 104px - var(--mobile-nav-height, 64px))';

  return (
    <>
      {/* Backdrop - only for clusters, not individual units */}
      <AnimatePresence>
        {isOpen && showBackdrop && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: backdropOpacity }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="fixed inset-0 bg-black pointer-events-auto z-[5] md:hidden"
            style={{
              top: '104px', // Backdrop covers entire mapbox area (below header 56px + filter 48px)
            }}
            onClick={closeDrawer}
          />
        )}
      </AnimatePresence>

      {/* Drawer */}
      <AnimatePresence>
        {isOpen && drawerContent && (
          <motion.div
            initial={{ y: '-100%', opacity: 0 }}
            animate={{
              y: `${drawerTransform}%`,
              opacity: 1,
            }}
            exit={{ y: '-100%', opacity: 0 }}
            transition={{
              type: 'tween',
              duration: isSwitchingUnits ? 0 : 0.3,
              ease: [0.25, 0.46, 0.45, 0.94], // iOS-style ease-out
            }}
            className="fixed left-0 right-0 bg-card dark:bg-card border-t border-border shadow-2xl z-[15] md:hidden overflow-hidden flex flex-col"
            style={{
              top: '104px', // Below header (56px) + filter row (48px)
              height: drawerHeight, // 40vh for individual units, full height for clusters
              maxHeight: drawerHeight,
            }}
          >
            {/* Content */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              <MobileInfoDrawerContent content={drawerContent} allUnits={allUnits} onClose={closeDrawer} />
            </div>
            
            {/* Close Button - Absolute Position */}
            <button 
              onClick={closeDrawer}
              className="absolute top-2 right-2 p-2 bg-background/80 backdrop-blur-sm rounded-full border border-border/50 shadow-sm z-20 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Drag Handle - at bottom */}
            <div
              {...bind()}
              className="border-t border-border bg-muted/50 backdrop-blur-sm cursor-grab active:cursor-grabbing pb-[calc(var(--safe-area-bottom)+2px)]"
              style={{ touchAction: 'none' }}
            >
              <div className="flex flex-col items-center justify-center h-3 gap-1 pt-1">
                <div className="w-12 h-1 bg-muted-foreground/40 rounded-full" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Custom scrollbar styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.3);
        }
      `}</style>
    </>
  );
}
